import { AccountInfo, IPublicClientApplication } from "@azure/msal-browser";
import { useMsal } from "@azure/msal-react";
import axios from "axios";
import { ALLOCATIONS_ROLES, hasAnyOfRoles } from "@/constants/appRoles";
import { PhoenixTokenResponse } from "@/types/AuthService";

export function getAppRolesFromAccount(account: AccountInfo | null): string[] {
  const claims = account?.idTokenClaims as { roles?: string[] } | undefined;
  const roles = claims?.roles;
  if (Array.isArray(roles)) return roles;
  return [];
}

export class AuthService {
  private gatewayUrl: string;

  constructor(gatewayUrl: string = import.meta.env.VITE_FLEX_GATEWAY_URL) {
    this.gatewayUrl = gatewayUrl;
  }

  public async acquireSsoToken(msalInstance: IPublicClientApplication): Promise<string> {
    const accounts = msalInstance.getAllAccounts();
    if (accounts.length === 0) {
      throw new Error("No accounts found. User must be logged in.");
    }

    const response = await msalInstance.acquireTokenSilent({
      scopes: ["User.Read"],
      account: accounts[0],
    });

    return response.accessToken;
  }

  public async exchangeForPhoenixToken(ssoToken: string): Promise<PhoenixTokenResponse> {
    const response = await axios.post<PhoenixTokenResponse>(
      `${this.gatewayUrl}/authorization/microsoft-sso-token`,
      { token: ssoToken },
      {
        headers: {
          "Content-Type": "application/json",
        },
      }
    );

    return response.data;
  }

  public async authenticate(
    msalInstance: IPublicClientApplication
  ): Promise<{ ssoToken: string; phoenixToken: PhoenixTokenResponse }> {
    const ssoToken = await this.acquireSsoToken(msalInstance);
    const phoenixToken = await this.exchangeForPhoenixToken(ssoToken);

    return { ssoToken, phoenixToken };
  }
}

export function useAuthService(): AuthService {
  return new AuthService();
}

export function useAppRoles(): string[] {
  const { instance } = useMsal();
  const accounts = instance.getAllAccounts();
  return getAppRolesFromAccount(accounts[0] ?? null);
}

export function useCanViewAllocations(): boolean {
  const roles = useAppRoles();
  return hasAnyOfRoles(roles, ALLOCATIONS_ROLES);
}
